import React, { useMemo, useState } from 'react';
import { t } from 'i18next';
import ModalPnLChart from './ModalPnLChart';
import { RawTrade } from '../function/type'; // Kiểm tra lại path import
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';

interface DayDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  trades?: RawTrade[];
}

// Hàm tính thời lượng giữa open_time và close_time
const calculateDuration = (openTime: string, closeTime: string) => {
  if (!openTime || !closeTime) return '00:00:00';
  const start = new Date(openTime).getTime();
  const end = new Date(closeTime).getTime();
  const diff = Math.max(0, end - start);

  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor(diff / (1000 * 60 * 60));

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const DayDetailModal: React.FC<DayDetailModalProps> = ({ isOpen, onClose, date, trades = [] }) => {
  // State lưu index của trade đang được xem chi tiết
  const [expandedTradeIndex, setExpandedTradeIndex] = useState<number | null>(null);

  // Tính toán chỉ số thống kê từ danh sách trades truyền vào
  const metricsData = useMemo(() => {
    const totalTrades = trades.length;
    if (totalTrades === 0) {
      return { pnl: 0, winRate: 0, profitFactor: 0, totalTrades: 0 };
    }

    let totalPnL = 0;
    let winningTrades = 0;
    let grossProfit = 0;
    let grossLoss = 0;

    trades.forEach(t => {
      const pnl = ((t.profit_gross || 0) + (t.commission || 0) + (t.swap || 0)) ;
      console.log('Trade PnL:', pnl, 'Gross Loss:', grossLoss, "totalPnL:", totalPnL, "winningTrades:", winningTrades);
      totalPnL += pnl;
      if (pnl > 0) {
        winningTrades++;
        grossProfit += pnl;
      } else if (pnl < 0) {
        grossLoss += Math.abs(pnl);
      }
      // if (pnl > 0) {
      //   winningTrades++;
      //   grossProfit += pnl;
      // } else if (pnl < 0) {
      //   grossLoss += Math.abs(pnl);
      // }
    });

    const winRate = (winningTrades / totalTrades) * 100;
    const profitFactor = grossLoss === 0 ? (grossProfit > 0 ? 999 : 0) : grossProfit / grossLoss;

    return {
      pnl: totalPnL,
      winRate,
      profitFactor,
      totalTrades
    };
  }, [trades]);

  const metrics = [
    {
      label: 'PnL',
      value: metricsData.pnl >= 0 ? `$${metricsData.pnl.toFixed(2)}` : `-$${Math.abs(metricsData.pnl).toFixed(2)}`,
      color: metricsData.pnl >= 0 ? '!text-teal-500' : '!text-rose-500'
    },
    { label: t('metrixLive.day.winRate'), value: `${metricsData.winRate.toFixed(2)}%` },
    { label: t('metrixLive.day.profitFactor'), value: metricsData.profitFactor.toFixed(2) },
    { label: t('metrixLive.day.countedTrades'), value: metricsData.totalTrades.toString() },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 transition-opacity duration-300">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col transform transition-transform duration-300">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h3 className="!text-xl font-bold !text-slate-900">{date}</h3>
          <button onClick={onClose} className="p-2 !text-slate-400 hover:!text-slate-700 hover:bg-slate-100 rounded-full transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
            <div className="lg:col-span-3 h-[200px] relative w-full border-b border-slate-100 pb-2">
              <ModalPnLChart trades={trades} />
            </div>
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              {metrics.map((m, idx) => (
                <div key={idx} className="bg-slate-50/80 rounded-xl p-4 flex flex-col items-center justify-center border border-slate-100 shadow-sm">
                  <span className="!text-[11px] font-bold !text-slate-500 mb-1 uppercase tracking-wider !text-center">{m.label}</span>
                  <span className={`!text-xl font-bold font-mono-num ${m.color || '!text-slate-800'}`}>{m.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-white">
                  <tr>
                    {[t('metrixLive.cols.order'), t('metrixLive.cols.closeTime'), t('metrixLive.cols.symbol'), t('metrixLive.cols.volume'), t('metrixLive.cols.duration'), 'PnL', ''].map((th, idx) => (
                      <th key={idx} scope="col" className={`px-6 py-4 !text-left !text-xs font-bold !text-slate-500 ${idx >= 3 && idx <= 4 ? '!text-center' : ''}`}>{th}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-50">
                  {trades.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-400 font-medium">
                        {t('metrixLive.day.empty')}
                      </td>
                    </tr>
                  ) : (
                    trades.map((trade, idx) => {
                      const grossPnL = (trade.profit_gross ?? 0) + (trade.commission || 0) + (trade.swap || 0);
                      const isLoss = grossPnL < 0;
                      const timeString = trade.close_time ? new Date(trade.close_time).toLocaleTimeString('vi-VN') : '--:--:--';
                      const sideFormatted = trade.side ? (trade.side.charAt(0).toUpperCase() + trade.side.slice(1).toLowerCase()) : 'Buy';
                      const duration = calculateDuration(trade.open_time, trade.close_time);
                      const isExpanded = expandedTradeIndex === idx;

                      return (
                        <React.Fragment key={idx}>
                          <tr className={`hover:bg-slate-50/50 transition-colors ${isExpanded ? 'bg-slate-50/80' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="!text-sm font-medium !text-slate-800 font-mono-num">
                                  #{trade.index || idx + 1}
                                </span>
                                <span className={`!text-xs font-bold ${sideFormatted.toLowerCase() === 'buy' ? '!text-teal-500' : '!text-rose-500'} flex items-center gap-1 mt-0.5`}>
                                  {sideFormatted.toLowerCase() === 'buy' ? (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 19L19 5M19 5v10M19 5H9"></path></svg>
                                  ) : (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 5L5 19M5 19v-10M5 19h10"></path></svg>
                                  )} {sideFormatted}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap !text-sm !text-slate-700 font-mono-num font-medium">{timeString}</td>
                            <td className="px-6 py-4 whitespace-nowrap !text-sm font-semibold !text-slate-800">{trade.symbol}</td>
                            <td className="px-6 py-4 whitespace-nowrap !text-center !text-sm font-medium !text-slate-600 font-mono-num">{trade.volume_lots_closed ?? trade.quantity_closed}</td>
                            <td className="px-6 py-4 whitespace-nowrap !text-center !text-sm !text-slate-600 font-mono-num font-medium">{duration}</td>
                            <td className={`px-6 py-4 whitespace-nowrap !text-left font-bold font-mono-num ${isLoss ? '!text-rose-500' : '!text-teal-500'}`}>
                              {isLoss ? `-$${Math.abs(grossPnL).toFixed(2)}` : `$${grossPnL.toFixed(2)}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap !text-right">
                              {/* NÚT BẤM XEM CHI TIẾT */}
                              <button
                                onClick={() => setExpandedTradeIndex(isExpanded ? null : idx)}
                                title={isExpanded ? t('metrixLive.hideDetails') : t('metrixLive.showDetails')}
                                className={`p-1.5 border rounded-md transition-colors shadow-sm ${isExpanded
                                  ? 'bg-slate-800 border-slate-800 !text-white'
                                  : 'bg-white border-slate-200 !text-slate-400 hover:!text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                {isExpanded ? (
                                  <CaretDownIcon className="w-4 h-4" />
                                ) : (
                                  <CaretUpIcon className="w-4 h-4" />
                                )
                                }
                              </button>
                            </td>
                          </tr>

                          {/* KHU VỰC HIỂN THỊ CHI TIẾT LỆNH (Mở rộng) */}
                          {isExpanded && (
                            <tr className="bg-slate-50/70">
                              <td colSpan={7} className="px-6 py-4">
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 text-xs">
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.openPrice')}</span>
                                    <span className="font-semibold text-slate-800 font-mono text-sm">{trade.open_price ?? '--'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.closePrice')}</span>
                                    <span className="font-semibold text-slate-800 font-mono text-sm">{trade.close_price ?? '--'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">Pips</span>
                                    <span className={`font-semibold font-mono text-sm ${trade.pips >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
                                      {trade.pips ?? '--'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.swap')}</span>
                                    <span className="font-semibold text-slate-800 font-mono text-sm">${trade.swap ?? 0}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.commission')}</span>
                                    <span className="font-semibold text-slate-800 font-mono text-sm">${(trade.commission || 0) + (trade.fee || 0)}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.grossProfit')}</span>
                                    <span className="font-semibold text-slate-800 font-mono text-sm">${((trade.profit_gross ?? 0)).toFixed(2)}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.openTime')}</span>
                                    <span className="font-medium text-slate-700 font-mono">{trade.open_time || '--'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.closeTime')}</span>
                                    <span className="font-medium text-slate-700 font-mono">{trade.close_time || '--'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.trade.balanceAfter')}</span>
                                    <span className="font-bold text-slate-900 font-mono text-sm">${trade.balance_after ?? '--'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block font-medium mb-1">{t('metrixLive.status')}</span>
                                    <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold uppercase text-[10px]">
                                      {trade.status || 'closed'}
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button onClick={onClose} className="px-8 py-2 bg-slate-200 hover:bg-slate-300 !text-slate-800 font-semibold rounded-md transition-colors shadow-sm">{t('metrixLive.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default DayDetailModal;